import {
  QueryCache,
  QueryClient,
  type EnsureInfiniteQueryDataOptions,
  type EnsureQueryDataOptions,
  type InfiniteData,
  type QueryKey,
} from "@tanstack/react-query";
import { ApiError } from "@repo/api-client";

/**
 * Every page is drawn from what was last read, then read again (0075).
 *
 * A loader warms its page's queries through `read`, and the page's component
 * reads them with `useSuspenseQuery`, so a fresh answer redraws it in place.
 * The copies live here, in memory, until the app is closed: never in storage.
 */

/**
 * A copy read in the last two seconds is not read again: long enough that a
 * page its loader has just read is not read twice as it draws, short enough
 * that every visit a person makes reads again.
 */
const FRESH_MS = 2_000;

let signedOutHandler = () => {};
let signingOut = false;

/**
 * What to do once a read says the session is gone: `main.tsx` passes the
 * router's revalidate, so the guards send the visitor to sign-in. Registered
 * rather than imported, since the router imports every page, and every page
 * this module.
 */
export function whenSignedOut(handler: () => void) {
  signedOutHandler = handler;
}

/**
 * Empties the cache and has the loaders run again, once however many reads
 * say so at once — every query on the page fails together: the session is
 * gone, or it is somebody else's.
 */
export function startAfresh() {
  if (signingOut) return;
  signingOut = true;
  queueMicrotask(() => {
    forgetEverything();
    signingOut = false;
    signedOutHandler();
  });
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (!(error instanceof ApiError) || error.status !== 401) return;
      // Only news while the cache holds somebody signed in: after signing
      // out, a page still drawn may ask once more, and that 401 is expected.
      // `["me"]` is `queries.me()`'s key, which this module cannot import.
      if (!queryClient.getQueryData(["me"])) return;
      startAfresh();
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: FRESH_MS,
      gcTime: Infinity,
      // A failure fails as fast as it did before the cache.
      retry: false,
      refetchOnWindowFocus: true,
    },
  },
});

/**
 * A loader's read. A copy is answered at once and read again behind it; a
 * copy a write has marked stale, or none, is waited for, so nobody sees a
 * page as it was before their own change.
 */
export function read<T, Key extends QueryKey>(query: EnsureQueryDataOptions<T, Error, T, Key>): Promise<T> {
  const state = queryClient.getQueryState<T>(query.queryKey);
  if (state?.isInvalidated) return readAfterWrite(query);
  return queryClient.ensureQueryData({ ...query, revalidateIfStale: true });
}

/**
 * A loader's read of a list paged by "Show more" (`infiniteQueryOptions`):
 * `read` for all the pages read so far. A copy is answered at once and read
 * again behind it; one a write has marked stale is waited for, every page of
 * it read again in turn, each from the cursor the page before it answered
 * with, so a change made on the third page shows there and nothing is shown
 * twice.
 */
export function readPages<T, P, Key extends QueryKey>(
  query: EnsureInfiniteQueryDataOptions<T, Error, T, Key, P>,
): Promise<InfiniteData<T, P>> {
  const state = queryClient.getQueryState(query.queryKey);
  // `_type` is what makes the query fetch all its pages: the observer sets it
  // too, once the page is drawn, but a loader reads before that.
  if (state?.isInvalidated) return readAfterWrite({ ...query, _type: "infinite" } as never);
  return queryClient.ensureInfiniteQueryData({ ...query, revalidateIfStale: true });
}

/**
 * A loader's read for a page that sends back what it shows — a form editing
 * something whole: never drawn from a copy, always waited for, so saving it
 * cannot put back what somebody else has changed since the copy was read.
 */
export function readFresh<T, Key extends QueryKey>(query: EnsureQueryDataOptions<T, Error, T, Key>): Promise<T> {
  const state = queryClient.getQueryState<T>(query.queryKey);
  if (state?.isInvalidated) return readAfterWrite(query);
  return queryClient.fetchQuery({ ...query, staleTime: 0 });
}

/** The copies read again since the last write. */
let readSinceWrite = new WeakSet<object>();

/**
 * A copy marked stale is read afresh. A read of it already on its way may
 * have begun before the write — one behind a copy, or as the window came
 * back — and its answer would be from before it: the first read since the
 * write cancels it, and whoever waited on it gets the new one's answer. Any
 * read after that waits on the new one.
 */
function readAfterWrite<T, Key extends QueryKey>(query: EnsureQueryDataOptions<T, Error, T, Key>): Promise<T> {
  const options = queryClient.defaultQueryOptions(query);
  const held = queryClient.getQueryCache().build(queryClient, options);
  const first = !readSinceWrite.has(held);
  readSinceWrite.add(held);
  return held.fetch(options, { cancelRefetch: first });
}

/**
 * Marks every copy stale, after anything that may have changed one: the
 * router's `dataStrategy` calls it after every action. Nothing is read again
 * here — the router's revalidation after the action does it, through `read`.
 */
export function markEverythingStale() {
  readSinceWrite = new WeakSet();
  void queryClient.invalidateQueries({ refetchType: "none" });
}

/**
 * Drops every copy: on signing in, signing out and deleting the account, so
 * nothing read as one person is ever drawn for another.
 */
export function forgetEverything() {
  queryClient.clear();
}
